import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { clean, num, stagger, stripHtml } from '../utils/format';
import { useIsEditing } from '../utils/hooks';
import { reviewMonth } from '../utils/reviews';
import { PATHS, topReviews, type Review } from '../utils/site';

const ctaDef: SlotGroupDefinition = {
  label: 'All Reviews Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Read All Reviews' },
    url: { label: 'URL', type: 'url', defaultValue: '/reviews' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** The three newest four-star-and-up reviews, title only; hides when there are none. */
export function Reviews() {
  const editing = useIsEditing();
  const cta = useSlotGroup('reviews.cta', ctaDef);
  const all = useCollectionBinding<Review>('reviews.items', { collection: 'reviews' });

  const top = topReviews(all).slice(0, 3);
  if (!top.length && !editing) return null;

  return (
    <section className="section reviews" id="reviews">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="reviews.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="reviews.title" as="h2" className="h2" />
        </div>
      </div>
      <EditableCollectionBinding
        bindingPath="reviews.items"
        label="Reviews"
        className="grid grid-cols-[repeat(3,1fr)] border-t border-t-[--q-border] max-[767px]:grid-cols-[1fr]"
      >
        {top.map((r, i) => (
          <article
            className="flex flex-col gap-5 border-l border-l-[--q-border] px-6 pb-0 pt-7 first:pl-0 first:[border-left:0] last:pr-0 max-[767px]:border-b max-[767px]:border-b-[--q-border] max-[767px]:px-0 max-[767px]:py-[22px] max-[767px]:[border-left:0] max-[767px]:[&:nth-child(n+3)]:hidden"
            key={i}
            data-reveal
            style={stagger(i)}
          >
            <div className="text-sm tracking-[.08em] text-[--q-secondary]" aria-label={`${num(r.rating) ?? 5} out of 5 stars`}>{'★'.repeat(Math.round(num(r.rating) ?? 5))}</div>
            <div className="is-clamped line-clamp-6 font-heading text-[20px] leading-[30px] max-[767px]:text-[18px] max-[767px]:leading-[27px]">{stripHtml(r.text)}</div>
            <div className="text-[13px] leading-[18px] text-muted-foreground">
              {clean(r.name) && <b className="font-medium text-[--q-ink]">{clean(r.name)} · </b>}Google
              {reviewMonth(r.date) ? ` · ${reviewMonth(r.date)}` : ''}
            </div>
          </article>
        ))}
      </EditableCollectionBinding>
      <div className="section__cta" data-reveal>
        <div className="section__buttons">
          <EditableSlotGroup groupPath="reviews.cta" definition={ctaDef}>
            <a className="btn btn-secondary" href={cta.url || PATHS.reviews} target={cta._target || '_self'}>
              {cta.label || 'Read All Reviews'} <span className="btn__arrow">→</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Reviews',
  displayName: 'Reviews',
  tokens: {
    'reviews.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Reviews · from Google</p>' },
    'reviews.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>What residents say about <span data-type="field-token" data-field="property_name"></span></p>',
      suggestedFields: ['property_reviews_blurb', 'property_home_reviews_blurb'],
    },
  },
  slotGroups: {
    'reviews.cta': ctaDef,
  },
  collectionBindings: {
    'reviews.items': {
      label: 'Reviews',
      collection: 'reviews',
    },
  },
};

(Reviews as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Reviews;
